/**
 * WorkflowProcessListRow - Linha da visualização em lista dos processos
 * -----------------------------------------------------------------------------
 * Mesmo desenho da `FlowListRow` (tela Fluxos): ícone, nome + descrição,
 * situação, etapas/ligações/versão, execuções e menu de ações.
 * O menu chega pronto da página para manter as mesmas regras de permissão.
 *
 * @version 1.0.0
 * RELEASE: 2026-10-06 v1.0.0 - Criação inicial (alternador Cards/Lista).
 */

import type { ReactNode } from 'react';
import { Activity, Boxes, Workflow as WorkflowIcon } from 'lucide-react';
import { StatusDotLabel } from '@/components/ui/status-dot-label';
import { cn } from '@/lib/utils';

interface Definicao {
  id: string;
  name: string;
  description?: string | null;
  root_entity_label?: string | null;
  root_entity_type?: string | null;
  status: string;
  node_count?: number | null;
  edge_count?: number | null;
  active_version_number?: number | null;
  instance_count?: number | null;
  open_instance_count?: number | null;
}

interface Props {
  definicao: Definicao;
  selo: { texto: string; tom: 'success' | 'neutral' | 'warning' };
  onOpen: () => void;
  /** Menu de ações (mesmo dos cards) */
  acoes: ReactNode;
}

export function WorkflowProcessListRow({ definicao: d, selo, onOpen, acoes }: Props) {
  const total = d.instance_count ?? 0;
  const abertas = d.open_instance_count ?? 0;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => e.key === 'Enter' && onOpen()}
      className={cn(
        'flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors hover:bg-muted/50',
        d.status === 'archived' && 'opacity-60',
      )}
    >
      {/* Ícone do processo */}
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted/50">
        <WorkflowIcon className="h-5 w-5 text-muted-foreground" />
      </div>

      {/* Nome + tipo do registro / descrição */}
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate font-medium">{d.name}</p>
          {d.root_entity_label && (
            <span className="hidden shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground sm:inline-flex">
              {d.root_entity_label}
            </span>
          )}
        </div>
        <p className="truncate text-sm text-muted-foreground">
          {d.description || d.root_entity_label || d.root_entity_type}
        </p>
      </div>

      <div className="shrink-0">
        <StatusDotLabel tone={selo.tom} label={selo.texto} />
      </div>

      {/* Estrutura do processo */}
      <div className="hidden w-40 shrink-0 items-center gap-1.5 text-sm text-muted-foreground md:flex">
        <Boxes className="h-4 w-4" />
        <span>
          {d.node_count ?? 0} etapas · {d.edge_count ?? 0} lig.
          {d.active_version_number ? ` · v${d.active_version_number}` : ''}
        </span>
      </div>

      {/* Execuções */}
      <div className="hidden w-44 shrink-0 items-center gap-1.5 text-sm text-muted-foreground lg:flex">
        <Activity className="h-4 w-4" />
        <span>
          {total} exec.
          {abertas > 0 ? ` · ${abertas} em andamento` : ''}
        </span>
      </div>

      <div onClick={(e) => e.stopPropagation()}>{acoes}</div>
    </div>
  );
}
